import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Ruler, 
  Edit,
  Trash2,
  FileText,
  Users,
  Receipt,
  DollarSign,
  TrendingUp,
  Split,
  GitBranch,
  FileSpreadsheet,
  Upload,
  Download,
  Mail,
  CheckCircle,
  XCircle,
  RotateCcw,
  Banknote,
  X,
  Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CurrencyDisplay } from '../components/ui/currency-display';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { projectsApi, transactionsApi, jvAgreementsApi, calculationsApi, ownersApi, profitAllocationsApi, subdivisionsApi, documentsApi } from '../lib/api';
import { formatCurrency, formatDate, getAgingLevel, useDismissedBanner } from '../lib/utils';
import { StatusBadge } from '../components/StatusBadge';
import { toast } from 'sonner';
import { TransactionFormDialog } from '../components/projects/TransactionFormDialog';
import SettlementStatementDialog from '../components/SettlementStatementDialog';
import { JVAgreementFormDialog } from '../components/projects/JVAgreementFormDialog';
import { ProjectFormDialog } from '../components/projects/ProjectFormDialog';
import { SubdivisionFormDialog } from '../components/projects/SubdivisionFormDialog';
import { EmailWorkbookDialog } from '../components/projects/EmailWorkbookDialog';
import { ChildLotManagerDialog } from '../components/projects/ChildLotManagerDialog';
import { SummaryTab } from '../components/projects/SummaryTab';
import { TransactionsTab } from '../components/projects/TransactionsTab';
import { JVAgreementsTab } from '../components/projects/JVAgreementsTab';
import { SubdivideMasterTab } from '../components/projects/SubdivideMasterTab';
import { CalculatorTab } from '../components/projects/CalculatorTab';
import { ReportsTab } from '../components/projects/ReportsTab';
import { DisbursementsTab } from '../components/projects/DisbursementsTab';
import { useAuth } from '../contexts/AuthContext';
import { generateInvestorStatement } from '../lib/pdfGenerator';

export const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, isSuperOwner } = useAuth();
  const [project, setProject] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [parentTransactions, setParentTransactions] = useState([]); // For subdivision children - parent's transactions
  const [jvAgreements, setJvAgreements] = useState([]);
  const [owners, setOwners] = useState([]);
  const [profitAllocation, setProfitAllocation] = useState(null);
  const [childProjects, setChildProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showEmailWorkbook, setShowEmailWorkbook] = useState(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  // Settlement Statement (HUD) capture — separate dialog per side. Operators
  // open it to enter a full HUD in one structured pass; backend writes the
  // atomic ledger and keeps project.purchase_price / sale_price aligned with
  // the deed amount.
  const [showSettlementDialog, setShowSettlementDialog] = useState(false);
  const [settlementSide, setSettlementSide] = useState('buy');
  // Wave-4 follow-up: removed `transactionMode` state — the dedicated
  // "Record Purchase" / "Record EMD" buttons were retired in favor of the
  // single "Add Transaction" entry point with a Type dropdown.
  const [showJVForm, setShowJVForm] = useState(false);
  const [showSubdivisionForm, setShowSubdivisionForm] = useState(false);
  const [editingJV, setEditingJV] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [waterfallData, setWaterfallData] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [roiData, setRoiData] = useState(null);

  // Dismissible banner state (scoped per project so dismissals on one
  // project don't hide warnings on another). localStorage-backed.
  const [acqMismatchDismissed, dismissAcqMismatch] = useDismissedBanner(`acquisition-date-mismatch:${id}`);
  const [duplicateBannerDismissed, dismissDuplicateBanner] = useDismissedBanner(`duplicate-transactions:${id}`);
  const [completedBannerDismissed, dismissCompletedBanner] = useDismissedBanner(`completed-banner:${id}`);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docFileInputRef = React.useRef(null);
  
  // QBO Import state
  const [showQBOImport, setShowQBOImport] = useState(false);
  const [qboImporting, setQBOImporting] = useState(false);
  const [qboImportResults, setQBOImportResults] = useState(null);
  const qboFileInputRef = React.useRef(null);
  
  // Status change dialog (for prompting to change to Owned after purchase price recorded)
  const [showStatusChangeDialog, setShowStatusChangeDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  
  // Child lot management state
  const [showChildLotManager, setShowChildLotManager] = useState(false);
  const [editingChildLot, setEditingChildLot] = useState(null);
  
  // Disbursement state
  const [disbursements, setDisbursements] = useState([]);
  const [disbursementSummary, setDisbursementSummary] = useState([]);
  const [showDisbursementForm, setShowDisbursementForm] = useState(false);
  const [disbursementData, setDisbursementData] = useState({ investor_name: '', amount: '', payment_method: 'Wire', payment_date: new Date().toISOString().split('T')[0], notes: '' });
  const [submittingDisbursement, setSubmittingDisbursement] = useState(false);
  const [dismissedBanner, setDismissedBanner] = useState(false);

  // Check for openJv query param after data loads
  useEffect(() => {
    if (!loading && searchParams.get('openJv') === 'true') {
      setShowJVForm(true);
      // Clear the query param after opening the form
      setSearchParams({});
    }
  }, [loading, searchParams, setSearchParams]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      const [projectRes, transactionsRes, ownersRes, allocationsRes] = await Promise.all([
        projectsApi.getOne(id),
        transactionsApi.getAll(id, true),
        ownersApi.getAll(),
        profitAllocationsApi.getAll(id)
      ]);
      setProject(projectRes.data);
      setTransactions(transactionsRes.data);
      setOwners(ownersRes.data);
      setProfitAllocation(allocationsRes.data.length > 0 ? allocationsRes.data[0] : null);

      // For subdivision children, get JV agreements from parent
      const jvProjectId = projectRes.data.parent_project_id || id;
      const jvRes = await jvAgreementsApi.getAll(jvProjectId);
      setJvAgreements(jvRes.data);
      
      // For subdivision children, also load parent's transactions for cost basis calculation
      if (projectRes.data.parent_project_id) {
        try {
          const parentTxRes = await transactionsApi.getAll(projectRes.data.parent_project_id);
          setParentTransactions(parentTxRes.data);
        } catch (e) {
          console.error('Failed to load parent transactions:', e);
        }
      }

      // Load child projects if this is a subdivision
      try {
        const childrenRes = await subdivisionsApi.getChildren(id);
        setChildProjects(childrenRes.data.children || []);
      } catch (e) {
        // Not a subdivision parent
        console.debug('No subdivision children for project:', id, e?.message);
      }

      // Load financial summary for sold/note-held projects
      // Note_Held is technically sold (owner-financed), the note is the asset
      if ((projectRes.data.status === 'Sold' || projectRes.data.status === 'Note_Held' || projectRes.data.status === 'Completed') && projectRes.data.sale_price) {
        try {
          const summaryRes = await projectsApi.getFinancialSummary(id);
          setFinancialSummary(summaryRes.data);
        } catch (e) {
          console.error('Failed to load financial summary:', e);
        }
        
        // Load ROI/IRR metrics
        try {
          const roiRes = await calculationsApi.roiIrr(id);
          setRoiData(roiRes.data);
        } catch (e) {
          console.error('Failed to load ROI/IRR:', e);
        }
      }

      if (jvRes.data.length > 0) {
        // Use capital-position for all projects (handles both sold and unsold)
        try {
          const capitalRes = await calculationsApi.capitalPosition(id);
          setWaterfallData(capitalRes.data);
        } catch (e) {
          // May not have all required data yet
          console.debug('Capital position not available yet:', e?.message);
        }
      }

      // Load documents
      try {
        const docsRes = await documentsApi.getProjectDocuments(id);
        setDocuments(docsRes.data);
      } catch (e) {
        // Documents may not be available
        console.debug('Documents not available for project:', id, e?.message);
      }
      
      // Load disbursements for sold JV projects
      if (jvRes.data.length > 0 && (projectRes.data.status === 'Sold' || projectRes.data.status === 'Note_Held' || projectRes.data.status === 'Completed')) {
        try {
          const [disbRes, disbSummaryRes] = await Promise.all([
            projectsApi.getDisbursements(id),
            projectsApi.getDisbursementSummary(id),
          ]);
          setDisbursements(disbRes.data);
          setDisbursementSummary(disbSummaryRes.data);
        } catch (e) {
          console.error('Failed to load disbursements:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load project:', error);
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDisbursement = async () => {
    if (!disbursementData.investor_name || !disbursementData.amount || !disbursementData.payment_method || !disbursementData.payment_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmittingDisbursement(true);
    try {
      await projectsApi.createDisbursement(id, {
        ...disbursementData,
        amount: parseFloat(disbursementData.amount),
      });
      toast.success(`Disbursement of $${parseFloat(disbursementData.amount).toLocaleString()} recorded`);
      setShowDisbursementForm(false);
      setDisbursementData({ investor_name: '', amount: '', payment_method: 'Wire', payment_date: new Date().toISOString().split('T')[0], notes: '' });
      // Reload disbursement data
      const [disbRes, disbSummaryRes] = await Promise.all([
        projectsApi.getDisbursements(id),
        projectsApi.getDisbursementSummary(id),
      ]);
      setDisbursements(disbRes.data);
      setDisbursementSummary(disbSummaryRes.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to record disbursement');
    } finally {
      setSubmittingDisbursement(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingDoc(true);
    try {
      await documentsApi.uploadDocument(id, file, 'jv_agreement', file.name);
      toast.success('Document uploaded');
      const docsRes = await documentsApi.getProjectDocuments(id);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (docFileInputRef.current) docFileInputRef.current.value = '';
    }
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const response = await documentsApi.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDocumentDelete = async (docId) => {
    try {
      await documentsApi.deleteDocument(docId);
      toast.success('Document deleted');
      setDocuments(documents.filter(d => d.id !== docId));
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleDelete = async () => {
    try {
      await projectsApi.delete(id);
      toast.success('Project deleted');
      navigate('/projects');
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      await transactionsApi.delete(transactionId);
      toast.success('Transaction deleted');
      loadData();
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      toast.error('Failed to delete transaction');
    }
  };

  const handleDeleteJV = async (jvId) => {
    try {
      await jvAgreementsApi.delete(jvId);
      toast.success('JV Agreement deleted');
      loadData();
    } catch (error) {
      console.error('Failed to delete JV agreement:', error);
      toast.error('Failed to delete JV agreement');
    }
  };

  const handleEditJV = (jv) => {
    setEditingJV(jv);
    setShowJVForm(true);
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setShowTransactionForm(true);
  };

  const handleGeneratePDF = () => {
    if (!waterfallData) {
      toast.error('Unable to generate statement - missing JV agreement');
      return;
    }
    try {
      generateInvestorStatement(project, waterfallData, jvAgreements[0]);
      toast.success(project.sale_price ? 'Investor statement downloaded' : 'Capital position statement downloaded');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleDownloadWorkbook = async () => {
    try {
      const response = await projectsApi.downloadWorkbook(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${(project.name || 'deal').replace(/[^A-Za-z0-9_-]+/g, '_')}_Workbook_Reconciliation.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Workbook reconciliation downloaded');
    } catch (error) {
      console.error('Failed to download workbook:', error);
      toast.error('Failed to download workbook');
    }
  };

  // QBO Import handlers
  const parseQBOCSV = (text) => {
    // Normalize line endings (Windows \r\n, old Mac \r, Unix \n)
    const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalizedText.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    // Parse a single row - handle quoted fields with commas inside
    const parseRow = (row) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim().replace(/^"|"$/g, '')); // Remove surrounding quotes
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim().replace(/^"|"$/g, ''));
      return result;
    };
    
    // Find the header row - QBO exports have multiple header rows
    // Look for a row that contains "Transaction date"
    let headerIndex = -1;
    for (let i = 0; i < Math.min(lines.length, 10); i++) {
      const line = lines[i].toLowerCase();
      if (line.includes('transaction date')) {
        headerIndex = i;
        break;
      }
    }
    
    // If no header found, try first row
    if (headerIndex === -1) {
      headerIndex = 0;
    }
    
    const headers = parseRow(lines[headerIndex]);
    const data = [];
    
    // Skip keywords - these indicate rows to ignore
    const skipKeywords = ['total', 'accrual basis', 'cash basis', 'inventory -', 'beginning balance'];
    
    // Date pattern - MM/DD/YY or MM/DD/YYYY
    const datePattern = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;
    
    // Process data rows after header
    for (let i = headerIndex + 1; i < lines.length; i++) {
      const values = parseRow(lines[i]);
      
      // Skip empty rows
      if (!values.some(v => v.trim())) {
        continue;
      }
      
      // Build row object first to check values
      const row = {};
      headers.forEach((header, index) => {
        if (header) {
          row[header] = values[index] || '';
        }
      });
      
      // Get the date value
      const dateVal = (row['Transaction date'] || '').trim();
      
      // Skip rows without a date
      if (!dateVal) {
        continue;
      }
      
      // Skip rows where date or first column contains skip keywords
      const firstCol = (values[0] || '').toLowerCase();
      if (skipKeywords.some(kw => dateVal.toLowerCase().includes(kw)) ||
          skipKeywords.some(kw => firstCol.includes(kw))) {
        continue;
      }
      
      // Validate date format - should be MM/DD/YYYY or similar
      if (!datePattern.test(dateVal)) {
        continue; // Skip rows without valid date
      }
      
      // Must have an amount
      const amount = (row['Amount'] || '').trim();
      if (!amount || amount === '0' || amount === '0.00') {
        continue;
      }
      
      data.push(row);
    }
    
    return data;
  };

  const handleQBOFileSelect = () => {
    qboFileInputRef.current?.click();
  };

  const handleQBOFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setQBOImporting(true);
    setQBOImportResults(null);

    try {
      const text = await file.text();
      const data = parseQBOCSV(text);
      
      if (data.length === 0) {
        toast.error('No transaction data found in CSV file. Make sure it\'s a QBO Transaction Detail export.');
        setQBOImporting(false);
        return;
      }

      const response = await transactionsApi.importQBO(id, data);
      setQBOImportResults(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`Imported ${response.data.success_count} transactions`);
        loadData(); // Reload transactions
      }
      
      if (response.data.error_count > 0) {
        toast.error(`${response.data.error_count} rows failed to import`);
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import transactions');
    } finally {
      setQBOImporting(false);
      e.target.value = ''; // Reset file input
    }
  };

  const downloadQBOTemplate = () => {
    const csv = `Date,Transaction Type,Num,Name,Memo/Description,Amount,Account,Category
2024-01-15,Check,1001,ABC Title Company,Title insurance for property,1500.00,Operating Account,Title Insurance
2024-01-16,Check,1002,Smith Surveying,Property survey,800.00,Operating Account,Survey
2024-01-20,Bill,,Jones Law Firm,Closing attorney fees,2000.00,Operating Account,Legal
2024-02-01,Expense,,Wire Transfer,Bank wire fee for closing,35.00,Operating Account,Bank Fees`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'qbo_transaction_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="project-loading">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!project) {
    return null;
  }

  // Check if property has been purchased by looking for a Purchase_Price transaction
  const hasPurchaseTransaction = transactions.some(t => t.type === 'Purchase_Price');
  
  // Calculate days - either "Days Held" (from acquisition after purchase) or "Days Under Contract" (from contract_date)
  const calculateDaysMetric = () => {
    // Dead deals don't need day counting
    if (project.status === 'Dead') {
      return { label: 'Days', days: '-', showAging: false };
    }

    // Parse a date string and return a Date object
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr.length === 10 && dateStr.includes('-')) {
        const [year, month, day] = dateStr.split('-');
        return new Date(year, month - 1, day);
      }
      return new Date(dateStr);
    };
    
    // For sold projects, calculate to sale_date; otherwise to today
    let endDate = new Date();
    if ((project.status === 'Sold' || project.status === 'Note_Held' || project.status === 'Completed') && project.sale_date) {
      endDate = parseDate(project.sale_date) || new Date();
    }
    
    // If property has been purchased (Purchase_Price transaction exists), show "Days Held"
    if (hasPurchaseTransaction && project.acquisition_date) {
      const acq = parseDate(project.acquisition_date);
      if (acq) {
        const diffTime = Math.abs(endDate - acq);
        return {
          label: 'Days Held',
          days: Math.ceil(diffTime / (1000 * 60 * 60 * 24)),
          showAging: true
        };
      }
    }
    
    // If not purchased but has contract_date, show "Days Under Contract"
    if (!hasPurchaseTransaction && project.contract_date) {
      const contractDate = parseDate(project.contract_date);
      if (contractDate) {
        const diffTime = Math.abs(endDate - contractDate);
        return {
          label: 'Days Under Contract',
          days: Math.ceil(diffTime / (1000 * 60 * 60 * 24)),
          showAging: false
        };
      }
    }
    
    return { label: 'Days', days: 0, showAging: false };
  };
  
  const daysMetric = calculateDaysMetric();
  const agingLevel = daysMetric.showAging ? getAgingLevel(daysMetric.days) : null;
  
  // Dead projects are fully locked from editing
  const isDead = project.status === 'Dead';

  // Check if this is a subdivision child lot
  const isChildLot = Boolean(project.parent_project_id);
  const costBasisPercentage = project.cost_basis_percentage || 100;
  const costBasisRatio = costBasisPercentage / 100;
  
  // Child's own expenses (transactions on child lot directly)
  const childOwnExpenses = transactions
    .filter(t => t.type === 'Expense')
    .reduce((sum, t) => sum + t.amount, 0);
  
  // Child's own reimbursements
  const childOwnReimbursements = transactions
    .filter(t => ['Reimbursement', 'Refund', 'Vendor_Reimbursement'].includes(t.type))
    .reduce((sum, t) => sum + t.amount, 0);
  
  // EMD amount from child (should be none for child lots)
  const totalEMD = transactions
    .filter(t => t.type === 'EMD')
    .reduce((sum, t) => sum + t.amount, 0);
  
  // For NON-child lots: Calculate cost basis from own transactions
  // For child lots: Calculate from parent's cost basis × percentage + child's own expenses
  let effectivePurchasePrice = 0;
  let costBasis = 0;
  let parentCostBasis = 0;
  let inheritedCostBasis = 0;
  
  if (isChildLot && parentTransactions.length > 0) {
    // Parent's total cost basis calculation
    const parentPurchasePrice = parentTransactions
      .filter(t => t.type === 'Purchase_Price')
      .reduce((sum, t) => sum + t.amount, 0);
    const parentExpenses = parentTransactions
      .filter(t => t.type === 'Expense')
      .reduce((sum, t) => sum + t.amount, 0);
    const parentReimbursements = parentTransactions
      .filter(t => ['Reimbursement', 'Refund', 'Vendor_Reimbursement'].includes(t.type))
      .reduce((sum, t) => sum + t.amount, 0);
    const parentEMD = parentTransactions
      .filter(t => t.type === 'EMD')
      .reduce((sum, t) => sum + t.amount, 0);
    
    // Parent cost basis includes EMD (money sent out as deposit)
    parentCostBasis = parentPurchasePrice + parentEMD + parentExpenses - parentReimbursements;
    
    // Child's inherited cost basis = parent's total × child's percentage
    inheritedCostBasis = parentCostBasis * costBasisRatio;
    
    // Total cost basis = inherited from parent + child's own expenses - child's reimbursements
    costBasis = inheritedCostBasis + childOwnExpenses - childOwnReimbursements;
    effectivePurchasePrice = inheritedCostBasis; // For display purposes
  } else {
    // Non-child lot: standard calculation
    const totalExpenses = transactions
      .filter(t => t.type === 'Expense')
      .reduce((sum, t) => sum + t.amount, 0);
    const totalReimbursements = transactions
      .filter(t => ['Reimbursement', 'Refund', 'Vendor_Reimbursement'].includes(t.type))
      .reduce((sum, t) => sum + t.amount, 0);
    const purchasePriceFromTx = transactions
      .filter(t => t.type === 'Purchase_Price')
      .reduce((sum, t) => sum + t.amount, 0);
    const emdFromTx = transactions
      .filter(t => t.type === 'EMD')
      .reduce((sum, t) => sum + t.amount, 0);
    
    // Cost Basis = Purchase Price + EMD + Expenses - Reimbursements
    // EMD is money sent out as deposit, part of total investment
    effectivePurchasePrice = purchasePriceFromTx + emdFromTx;
    costBasis = effectivePurchasePrice + totalExpenses - totalReimbursements;
  }
  
  // Total expenses/reimbursements for display (child's own)
  const totalExpenses = childOwnExpenses;
  const totalReimbursements = childOwnReimbursements;
  
  // Show expected purchase price from contract (view-only reference) - only for non-child lots
  const contractPurchasePrice = isChildLot ? 0 : (project.purchase_price || 0);

  // Calculate financial summary for sold properties
  // Note_Held is also "sold" - just owner-financed with the note as an asset
  const isSold = project.status === 'Sold' || project.status === 'Note_Held' || project.status === 'Completed';
  const isCompleted = project.status === 'Completed';
  const hasJV = jvAgreements.length > 0;
  
  // Calculate gross profit
  const grossProfit = (project.sale_price || 0) - costBasis;
  
  // Land finder profit (from waterfall or gross if no JV)
  const landFinderProfit = waterfallData?.land_finder_profit || (hasJV ? 0 : grossProfit);

  // ── Data integrity audit (per Land Finder JV Financial SOP audit) ──
  // 1) Duplicate transaction detection. Designed to catch the audit's
  //    "$1,500 Soil Study entered twice" pattern WITHOUT false-flagging
  //    legitimate recurring small fees (postage, $1.50 check delivery,
  //    parking, etc.). Two transactions are flagged as probable duplicates
  //    only when ALL of the following hold:
  //      • amount ≥ $100 (filter out recurring pocket-change fees)
  //      • same payer + same expense bucket
  //      • within 14 days of each other (most data-entry mistakes happen
  //        when re-entering an invoice within a couple weeks)
  //      • AND either identical date OR identical (case-insensitive)
  //        description — at least one strong signal beyond amount alone.
  const duplicateTxnGroups = (() => {
    if (!isOwner || !transactions || transactions.length < 2) return [];
    const MIN_AMOUNT = 100;             // skip small recurring fees
    const WINDOW_DAYS = 14;             // narrow window — real dupe entries cluster
    const DAY_MS = 24 * 60 * 60 * 1000;

    const expenses = transactions.filter(
      t => t.type === 'Expense' && (t.amount || 0) >= MIN_AMOUNT
    );
    const buckets = {};
    for (const t of expenses) {
      const key = `${(t.amount || 0).toFixed(2)}|${t.responsible_party || ''}|${t.expense_bucket || t.category || ''}`;
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(t);
    }
    const norm = (s) => (s || '').trim().toLowerCase();
    const groups = [];
    for (const list of Object.values(buckets)) {
      if (list.length < 2) continue;
      const sorted = [...list].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
      const flagged = new Set();
      for (let i = 0; i < sorted.length - 1; i++) {
        for (let j = i + 1; j < sorted.length; j++) {
          const a = sorted[i], b = sorted[j];
          const dA = new Date(a.date || 0).getTime();
          const dB = new Date(b.date || 0).getTime();
          if (Math.abs(dB - dA) > WINDOW_DAYS * DAY_MS) break; // sorted; no further matches
          // Strong signal #1: same date — almost certainly a re-entry mistake
          const sameDate = (a.date || '').split('T')[0] === (b.date || '').split('T')[0]
            && !!(a.date);
          // Strong signal #2: identical description text
          const sameDesc = norm(a.description) === norm(b.description) && norm(a.description) !== '';
          if (sameDate || sameDesc) {
            flagged.add(a);
            flagged.add(b);
          }
        }
      }
      if (flagged.size >= 2) groups.push([...flagged]);
    }
    return groups;
  })();

  // 2) Acquisition date sanity check: should match the date of the largest
  //    Purchase_Price transaction (HUD closing). Surfaces the audit's 3/19
  //    vs 5/8 discrepancy.
  const acquisitionDateMismatch = (() => {
    if (!isOwner || !transactions || transactions.length === 0 || !project.acquisition_date) return null;
    const ppTxns = transactions.filter(t => t.type === 'Purchase_Price' && t.amount > 0);
    if (ppTxns.length === 0) return null;
    const largestPP = ppTxns.reduce((m, t) => (t.amount > (m?.amount || 0) ? t : m), null);
    if (!largestPP || !largestPP.date) return null;
    const ppDate = (largestPP.date || '').split('T')[0];
    const acqDate = (project.acquisition_date || '').split('T')[0];
    if (ppDate === acqDate) return null;
    // Calculate days difference for severity flag
    const daysDiff = Math.abs((new Date(ppDate) - new Date(acqDate)) / (1000 * 60 * 60 * 24));
    return { ppDate, acqDate, daysDiff: Math.round(daysDiff) };
  })();

  return (
    <div className="space-y-6" data-testid="project-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="flex items-start gap-3 sm:gap-4 min-w-0">
          <Link to="/projects" className="flex-shrink-0 mt-1">
            <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8 sm:h-10 sm:w-10" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </Link>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="text-lg sm:text-2xl font-heading font-bold truncate">{project.name}</h1>
              <StatusBadge status={project.status} />
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-xs sm:text-sm text-muted-foreground">
              {project.county && project.state && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                  {project.county}, {project.state}
                </span>
              )}
              {project.acres && (
                <span className="flex items-center gap-1">
                  <Ruler className="w-3 h-3 sm:w-4 sm:h-4" />
                  {Number(project.acres).toFixed(2)} acres
                </span>
              )}
              {project.terranotes_id && (
                <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded-sm">
                  TN: {project.terranotes_id}
                </span>
              )}
              {project.apn && (
                <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded-sm">
                  APN: {project.apn}
                </span>
              )}
              {project.number_of_lots && (
                <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded-sm">
                  {project.number_of_lots} lots
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-11 sm:ml-0">
          {isOwner && !isCompleted && (
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => setShowEditForm(true)}
              data-testid="edit-project-btn"
            >
              <Edit className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Edit</span>
            </Button>
          )}
          {isOwner && (
            <Button
              variant="outline"
              className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={handleDownloadWorkbook}
              title="Download the 4-sheet Workbook Reconciliation Excel for this deal"
              data-testid="download-workbook-btn"
            >
              <Download className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Workbook</span>
            </Button>
          )}
          {isOwner && (
            <Button
              variant="outline"
              className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => setShowEmailWorkbook(true)}
              title="Email the workbook reconciliation to the investor (with preview)"
              data-testid="email-workbook-btn"
            >
              <Mail className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Email</span>
            </Button>
          )}
          {isOwner && !isDead && (
            <Button
              variant="outline"
              className="rounded-sm border-[#C68E5D]/50 text-[#C68E5D] hover:bg-[#C68E5D]/10"
              onClick={() => navigate(`/proposals?project_id=${project.id}`)}
              data-testid="create-proposal-btn"
            >
              <FileText className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Create Proposal</span>
            </Button>
          )}
          {isOwner && isDead && (
            <Button
              variant="outline"
              className="rounded-sm border-amber-300 text-red-600 hover:bg-amber-50"
              onClick={async () => {
                if (!window.confirm('Reverse this dead deal? It will be moved back to Under Contract (Buy).')) return;
                try {
                  // Ensure required fields exist for Under_Contract status
                  const updates = { status: 'Under_Contract' };
                  if (!project.contract_date) {
                    updates.contract_date = new Date().toISOString().split('T')[0];
                  }
                  if (!project.estimated_close_date) {
                    const est = new Date();
                    est.setMonth(est.getMonth() + 3);
                    updates.estimated_close_date = est.toISOString().split('T')[0];
                  }
                  await projectsApi.update(project.id, updates);
                  toast.success('Deal reversed to Under Contract (Buy). You can update the dates in Edit.');
                  loadData();
                } catch (error) {
                  toast.error(error?.response?.data?.detail || 'Failed to reverse deal');
                }
              }}
              data-testid="reverse-dead-deal-btn"
            >
              <RotateCcw className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Reverse Deal</span>
            </Button>
          )}
          {isOwner && project.strategy === 'Subdivide' && !isDead && project.status !== 'Sold' && childProjects.length === 0 && (
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => setShowSubdivisionForm(true)}
              data-testid="subdivide-project-btn"
            >
              <Split className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Subdivide</span>
            </Button>
          )}
          {isSuperOwner && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-sm text-red-600 hover:bg-red-50" data-testid="delete-project-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-sm">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                    <Trash2 className="w-5 h-5" />
                    Delete Project
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-3">
                    <p>Are you sure you want to delete <strong>&quot;{project.name}&quot;</strong>?</p>
                    <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm">
                      <p className="font-medium text-red-800 mb-2">⚠️ Warning: This action is permanent and will delete:</p>
                      <ul className="text-red-700 text-xs space-y-1">
                        <li>• All project details and financial data</li>
                        <li>• {transactions.length} transaction(s) associated with this project</li>
                        <li>• {jvAgreements.length} JV agreement(s) and investor records</li>
                        {childProjects.length > 0 && <li>• {childProjects.length} subdivided child lot(s)</li>}
                        <li>• All reports and calculations for this project</li>
                      </ul>
                      <p className="mt-2 text-red-800 font-medium">This data cannot be recovered after deletion.</p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700 rounded-sm"
                    data-testid="confirm-delete-btn"
                  >
                    Yes, Delete Project
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Data integrity banners (per Land Finder JV Financial SOP audit) */}
      {acquisitionDateMismatch && !acqMismatchDismissed && (
        <div className="p-3 rounded-sm bg-amber-50 border border-amber-200 flex items-start gap-2"
             data-testid="acquisition-date-mismatch-banner">
          <span className="text-amber-600 text-base leading-none">⚠</span>
          <div className="flex-1">
            <p className="text-xs font-semibold text-amber-900">Acquisition date doesn't match Purchase transaction</p>
            <p className="text-[11px] text-amber-800 mt-0.5">
              Project's Acquisition Date is <strong>{acquisitionDateMismatch.acqDate}</strong> but the largest
              Purchase Price transaction is dated <strong>{acquisitionDateMismatch.ppDate}</strong>
              {' '}({acquisitionDateMismatch.daysDiff} days apart). The HUD closing date should be the
              acquisition date — this affects Days Held calculations and the active tier on profit splits.
              {isOwner && ' Click "Edit" above to fix.'}
            </p>
          </div>
          <button
            onClick={dismissAcqMismatch}
            className="text-amber-500 hover:text-amber-700 p-1 flex-shrink-0"
            aria-label="Dismiss banner"
            data-testid="dismiss-acquisition-date-mismatch-banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {duplicateTxnGroups.length > 0 && !duplicateBannerDismissed && (
        <div className="p-3 rounded-sm bg-rose-50 border border-rose-200 flex items-start gap-2"
             data-testid="duplicate-transactions-banner">
          <span className="text-rose-600 text-base leading-none">⚠</span>
          <div className="flex-1">
            <p className="text-xs font-semibold text-rose-900">
              {duplicateTxnGroups.reduce((s, g) => s + g.length, 0)} potential duplicate transaction(s) detected
            </p>
            <p className="text-[11px] text-rose-800 mt-0.5">
              These transactions share the same amount + payer + bucket AND match on either
              date or description — strong signal one was entered twice. Duplicates inflate
              cost basis and skew profit splits at sale. Review and delete any extras from
              the Transactions tab.
            </p>
            <ul className="mt-1 space-y-0.5">
              {duplicateTxnGroups.flatMap((group, gi) => group.map((t, ti) => (
                <li key={`${gi}-${ti}-${t.id}`} className="text-[10px] text-rose-700 font-mono">
                  {(t.date || '').split('T')[0]} • {t.category || t.expense_bucket} • {(t.responsible_party || '—')} • ${(t.amount || 0).toLocaleString()} {t.description ? `— ${t.description}` : ''}
                </li>
              )))}
            </ul>
          </div>
          <button
            onClick={dismissDuplicateBanner}
            className="text-rose-500 hover:text-rose-700 p-1 flex-shrink-0"
            aria-label="Dismiss banner"
            data-testid="dismiss-duplicate-transactions-banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Completed/Finalized Banner */}
      {isCompleted && !completedBannerDismissed && (
        <Card className="rounded-sm border bg-slate-50 border-slate-300" data-testid="completed-banner">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-slate-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-slate-800">This deal is finalized</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Financial data, transactions, profit allocations, and commissions are locked. QBO sync is disabled. You can still add manual transactions.
                  {project.completed_at && <span> — Completed {new Date(project.completed_at).toLocaleDateString()}</span>}
                </p>
              </div>
              <button
                onClick={dismissCompletedBanner}
                className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0"
                aria-label="Dismiss banner"
                data-testid="dismiss-completed-banner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Child Projects Banner (if subdivision) */}
      {childProjects.length > 0 && (
        <Card className="rounded-sm border bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <GitBranch className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="font-medium text-blue-800">Subdivided into {childProjects.length} lots</p>
                    <span className="text-xs text-blue-600">
                      ({childProjects.filter(c => ['Sold_Cash', 'Sold_Note'].includes(c.subdivision_sale_status) || ['Sold', 'Note_Held', 'Completed'].includes(c.status)).length} sold)
                    </span>
                  </div>
                  {/* Child lot grid with status and value */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {childProjects.map(child => {
                      // A child lot can be marked sold two ways: via its own
                      // project status (Sold / Note_Held / Completed) OR via the
                      // legacy subdivision_sale_status sub-flag. Prefer the actual
                      // project status so the card never shows a stale
                      // "Under Contract" badge on a lot that's really sold.
                      const rawSaleStatus = child.subdivision_sale_status || 'None';
                      const projectStatusSold = ['Sold', 'Note_Held', 'Completed'].includes(child.status);
                      const isSold = projectStatusSold || rawSaleStatus.startsWith('Sold');
                      const isUnderContract = !isSold && (child.status === 'Under_Contract' || rawSaleStatus === 'Under_Contract');
                      const isListed = !isSold && !isUnderContract && rawSaleStatus === 'Listed';
                      const statusLabel = isSold ? 'Sold'
                        : isUnderContract ? 'Under Contract'
                        : isListed ? 'Listed'
                        : 'Available';
                      return (
                        <div 
                          key={child.id} 
                          className={`p-2 rounded-sm border ${
                            isSold ? 'bg-emerald-50 border-emerald-200' :
                            isListed ? 'bg-yellow-50 border-yellow-200' :
                            isUnderContract ? 'bg-amber-50 border-amber-200' :
                            'bg-white border-blue-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <Link to={`/projects/${child.id}`} className="font-medium text-sm hover:underline">
                              {child.name}
                            </Link>
                            <Badge 
                              variant="outline" 
                              className={`text-[9px] rounded-sm ${
                                isSold ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                                isListed ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                                isUnderContract ? 'bg-amber-100 text-red-600 border-amber-300' :
                                'bg-gray-100 text-gray-600 border-gray-300'
                              }`}
                              data-testid={`child-lot-status-${child.id}`}
                            >
                              {statusLabel}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between mt-1 text-xs">
                            <span className="text-muted-foreground">{child.cost_basis_percentage}% • {formatCurrency(child.purchase_price)}</span>
                            {child.sale_price > 0 && (
                              <span className="font-mono text-emerald-600">{formatCurrency(child.sale_price)}</span>
                            )}
                          </div>
                          {isOwner && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="w-full mt-2 h-6 text-xs text-blue-600 hover:text-emerald-600 hover:bg-blue-100"
                              onClick={() => {
                                setEditingChildLot(child);
                                setShowChildLotManager(true);
                              }}
                              data-testid={`manage-child-${child.id}`}
                            >
                              <Edit className="w-3 h-3 mr-1" /> Manage
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parent Project Banner (if this is a child lot) */}
      {project.parent_project_id && (
        <Card className="rounded-sm border bg-amber-50 border-amber-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <GitBranch className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="text-sm text-red-600">
                    This is a subdivided lot • Cost Basis: {project.cost_basis_percentage}% of parent
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Contract date, acquisition date, and strategy are inherited from parent and cannot be edited here.
                  </p>
                  <Link to={`/projects/${project.parent_project_id}`} className="text-sm text-amber-800 underline">
                    View Parent Project
                  </Link>
                </div>
              </div>
              {jvAgreements.length > 0 && (
                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 rounded-sm">
                  JV: {jvAgreements[0]?.investor_name} (inherited from parent)
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card className="rounded-sm border" data-testid="purchase-price-card">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">Purchase Price</p>
            <p className="text-base sm:text-xl font-heading font-bold mt-1 font-mono">
              {formatCurrency(project.purchase_price)}
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="sale-price-card">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">
              {project.sale_price ? 'Sale Price' : 'Est. Sale Price'}
            </p>
            <p className={`text-base sm:text-xl font-heading font-bold mt-1 font-mono ${!project.sale_price && project.estimated_sale_price ? 'text-muted-foreground' : ''}`}>
              {project.sale_price
                ? formatCurrency(project.sale_price)
                : project.estimated_sale_price
                  ? formatCurrency(project.estimated_sale_price)
                  : '-'}
            </p>
            {project.sale_date && (
              <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(project.sale_date)}
              </p>
            )}
            {!project.sale_price && project.estimated_sale_price && (
              <p className="text-[10px] sm:text-xs text-amber-600 mt-1">Estimated</p>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="days-held-card">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">{daysMetric.label}</p>
            <p className={`text-base sm:text-xl font-heading font-bold mt-1 font-mono ${agingLevel ? `aging-${agingLevel}` : ''}`}>
              {daysMetric.days} days
            </p>
            {agingLevel === 'critical' && (
              <p className="text-[10px] sm:text-xs text-red-600 mt-1">Approaching JV tier cliff!</p>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-sm border" data-testid="cost-basis-card">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium">Cost Basis</p>
            {costBasis > 0 || (isChildLot && parentCostBasis > 0) ? (
              <>
                <CurrencyDisplay amount={costBasis} size="xl" className="mt-1 font-heading text-red-600" />
                {isChildLot ? (
                  // Child lot cost basis breakdown
                  <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                    <p className="text-purple-600">
                      Inherited ({costBasisPercentage}% of parent): {formatCurrency(inheritedCostBasis)}
                    </p>
                    {totalExpenses > 0 && (
                      <p>+ Child Expenses: {formatCurrency(totalExpenses)}</p>
                    )}
                    {totalReimbursements > 0 && (
                      <p className="text-emerald-600">- Refunds: {formatCurrency(totalReimbursements)}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1 pt-1 border-t border-dashed">
                      Parent Total Cost Basis: {formatCurrency(parentCostBasis)}
                    </p>
                  </div>
                ) : (
                  // Regular project cost basis breakdown
                  <p className="text-xs text-muted-foreground mt-1">
                    Purchase + EMD: {formatCurrency(effectivePurchasePrice)} + Expenses: {formatCurrency(totalExpenses)}
                    {totalReimbursements > 0 && (
                      <span className="text-emerald-600"> - Refunds: {formatCurrency(totalReimbursements)}</span>
                    )}
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-xl font-heading font-bold mt-1 font-mono text-muted-foreground">$0</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {isChildLot 
                    ? "Waiting for parent transactions to calculate cost basis"
                    : "No transactions recorded yet"
                  }
                </p>
              </>
            )}
            {/* Show contract price hint only for non-child lots */}
            {!isChildLot && contractPurchasePrice > 0 && effectivePurchasePrice === 0 && (
              <p className="text-xs text-blue-600 mt-2">
                Contract price: {formatCurrency(contractPurchasePrice)} (not yet funded)
              </p>
            )}
            {/* EMD breakdown - show separately for clarity */}
            {totalEMD > 0 && !isChildLot && (
              <p className="text-xs text-blue-600 mt-1">
                (includes EMD: {formatCurrency(totalEMD)})
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      {/* Investor Disbursement Banner - dismissible notification */}
      {!isOwner && isSold && disbursements.length > 0 && !dismissedBanner && (
        <Card className="rounded-sm border-emerald-200 bg-emerald-50" data-testid="disbursement-top-banner">
          <CardContent className="py-3">
            <div className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-emerald-800 text-sm">
                  A payment has been sent to you
                </p>
                <p className="text-xs text-emerald-600">
                  View the Disbursements tab for details
                </p>
              </div>
              <button
                onClick={() => setDismissedBanner(true)}
                className="text-emerald-400 hover:text-emerald-600 p-1"
                data-testid="dismiss-disbursement-banner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {project.migrated_to_master && project.master_project_id && (
        <div className="mb-4 p-4 rounded-sm border border-amber-300 bg-amber-50 flex items-center justify-between gap-3" data-testid="migrated-child-banner">
          <p className="text-sm text-amber-800">
            This lot is now part of a <strong>Subdivide Master</strong> deal. Its financials live on the master deal's <strong>Lots &amp; Closings</strong> tab.
          </p>
          <Button size="sm" className="rounded-sm shrink-0" onClick={() => navigate(`/projects/${project.master_project_id}`)} data-testid="go-to-master-btn">
            Go to Master Deal
          </Button>
        </div>
      )}

      <Tabs defaultValue={project.is_subdivide_master ? "subdivide" : (isSold ? "summary" : "transactions")} className="space-y-4">
        <TabsList className="bg-muted/50 rounded-sm w-full overflow-x-auto flex justify-start">
          {isSold && (
            <TabsTrigger value="summary" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-summary">
              <DollarSign className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Financial Summary</span><span className="sm:hidden">Summary</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="transactions" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-transactions">
            <Receipt className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Transactions</span><span className="sm:hidden">Txns</span>
          </TabsTrigger>
          <TabsTrigger value="jv" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-jv">
            <Users className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">JV Agreements</span><span className="sm:hidden">JV</span> {jvAgreements.length > 0 && `(${jvAgreements.length})`}
          </TabsTrigger>
          {project.strategy === 'Subdivide' && (
            <TabsTrigger value="subdivide" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-subdivide">
              <Split className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Lots &amp; Closings</span><span className="sm:hidden">Lots</span>
            </TabsTrigger>
          )}
          {isOwner && (
            <TabsTrigger value="calculator" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-calculator">
              <TrendingUp className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Calculator</span><span className="sm:hidden">Calc</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="reports" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-reports">
            <FileText className="w-4 h-4 sm:mr-2" /> Reports
          </TabsTrigger>
          {isSold && hasJV && (
            <TabsTrigger value="disbursements" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-disbursements">
              <Banknote className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Disbursements</span><span className="sm:hidden">Payouts</span>
              {disbursementSummary.some(s => !s.fully_disbursed) && (
                <span className="ml-1 w-2 h-2 rounded-full bg-amber-500 inline-block"></span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Financial Summary Tab */}
        <SummaryTab
          project={project}
          financialSummary={financialSummary}
          roiData={roiData}
          waterfallData={waterfallData}
          jvAgreements={jvAgreements}
          owners={owners}
          profitAllocation={profitAllocation}
          transactions={transactions}
          daysMetric={daysMetric}
          isOwner={isOwner}
          isSold={isSold}
          hasJV={hasJV}
          costBasis={costBasis}
          totalExpenses={totalExpenses}
          grossProfit={grossProfit}
          landFinderProfit={landFinderProfit}
        />

        {/* Transactions Tab */}
        <TransactionsTab
          project={project}
          transactions={transactions}
          childLots={childProjects}
          jvAgreements={jvAgreements}
          isOwner={isOwner}
          isSuperOwner={isSuperOwner}
          isCompleted={isCompleted}
          onEditTransaction={handleEditTransaction}
          onDeleteTransaction={handleDeleteTransaction}
          onAddTransaction={() => {
            setEditingTransaction(null);
            setShowTransactionForm(true);
          }}
          onOpenQBOImport={() => setShowQBOImport(true)}
          onOpenSettlement={(side) => { setSettlementSide(side); setShowSettlementDialog(true); }}
        />

        {/* JV Agreements Tab */}
        <JVAgreementsTab
          jvAgreements={jvAgreements}
          waterfallData={waterfallData}
          documents={documents}
          project={project}
          isOwner={isOwner}
          isSuperOwner={isSuperOwner}
          isDead={isDead}
          onEditJV={handleEditJV}
          onDeleteJV={handleDeleteJV}
          onAddJV={() => { setEditingJV(null); setShowJVForm(true); }}
          onDocumentUpload={handleDocumentUpload}
          onDocumentDownload={handleDocumentDownload}
          onDocumentDelete={handleDocumentDelete}
          docFileInputRef={docFileInputRef}
          uploadingDoc={uploadingDoc}
          loadData={loadData}
        />

        {/* Subdivide Master Tab — Lots, Cap Table & Closings */}
        {project.strategy === 'Subdivide' && (
          <SubdivideMasterTab
            projectId={project.id}
            project={project}
            isOwner={isOwner}
            isSuperOwner={isSuperOwner}
          />
        )}

        {/* Calculator Tab */}
        {isOwner && (
          <CalculatorTab
            project={project}
            transactions={transactions}
            jvAgreements={jvAgreements}
          />
        )}

        {/* Reports Tab */}
        <ReportsTab
          project={project}
          waterfallData={waterfallData}
          onGeneratePDF={handleGeneratePDF}
        />

        {/* Disbursements Tab */}
        {isSold && hasJV && (
          <DisbursementsTab
            isOwner={isOwner}
            disbursements={disbursements}
            disbursementSummary={disbursementSummary}
            onRecordDisbursement={() => setShowDisbursementForm(true)}
          />
        )}
      </Tabs>

      {/* Disbursement Form Dialog */}
      <Dialog open={showDisbursementForm} onOpenChange={setShowDisbursementForm}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-forest-500" />
              Record Disbursement
            </DialogTitle>
            <DialogDescription>
              Record a payment sent to an investor for this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Investor *</Label>
              <Select
                value={disbursementData.investor_name}
                onValueChange={(val) => setDisbursementData(prev => ({ ...prev, investor_name: val }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="disbursement-investor-select">
                  <SelectValue placeholder="Select investor..." />
                </SelectTrigger>
                <SelectContent>
                  {disbursementSummary.map(s => (
                    <SelectItem key={s.investor_name} value={s.investor_name}>
                      {s.investor_name} — {formatCurrency(s.remaining)} remaining
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                type="text"
                placeholder="0.00"
                value={disbursementData.amount ? Number(disbursementData.amount).toLocaleString('en-US') : ''}
                onChange={(e) => setDisbursementData(prev => ({ ...prev, amount: e.target.value.replace(/[^0-9.]/g, '') }))}
                className="rounded-sm"
                data-testid="disbursement-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <Select
                value={disbursementData.payment_method}
                onValueChange={(val) => setDisbursementData(prev => ({ ...prev, payment_method: val }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="disbursement-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Wire">Wire Transfer</SelectItem>
                  <SelectItem value="ACH">ACH Transfer</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={disbursementData.payment_date}
                onChange={(e) => setDisbursementData(prev => ({ ...prev, payment_date: e.target.value }))}
                className="rounded-sm"
                data-testid="disbursement-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                type="text"
                placeholder="e.g. Final payout, Partial return of capital..."
                value={disbursementData.notes}
                onChange={(e) => setDisbursementData(prev => ({ ...prev, notes: e.target.value }))}
                className="rounded-sm"
                data-testid="disbursement-notes-input"
              />
            </div>
            <Button
              onClick={handleSubmitDisbursement}
              disabled={submittingDisbursement || !disbursementData.investor_name || !disbursementData.amount}
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
              data-testid="submit-disbursement-btn"
            >
              {submittingDisbursement ? 'Recording...' : 'Record Disbursement'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogs */}
      <ProjectFormDialog
        open={showEditForm}
        onOpenChange={setShowEditForm}
        project={project}
        onSuccess={(updated) => {
          // Always reload full data when status changes (financial summary, waterfall, etc.)
          if (updated?.reload || updated?.status !== project?.status) {
            loadData();
          } else {
            setProject(updated);
          }
          setShowEditForm(false);
          toast.success('Project updated');
        }}
      />

      <TransactionFormDialog
        open={showTransactionForm}
        onOpenChange={(open) => {
          setShowTransactionForm(open);
          if (!open) {
            setEditingTransaction(null);
          }
        }}
        projectId={id}
        transaction={editingTransaction}
        jvAgreements={jvAgreements}
        isSubdivide={project?.strategy === 'Subdivide'}
        onSuccess={(result) => {
          loadData();
          setShowTransactionForm(false);
          setEditingTransaction(null);
          toast.success(editingTransaction ? 'Transaction updated' : 'Transaction added');
          
          // If this was a new Purchase_Price transaction and project is not Owned, prompt to change status
          if (result?.isNew && result?.type === 'Purchase_Price' && project?.status !== 'Owned' && project?.status !== 'Sold' && project?.status !== 'Note_Held') {
            setShowStatusChangeDialog(true);
            setPendingStatusChange('Owned');
          }
        }}
      />

      <JVAgreementFormDialog
        open={showJVForm}
        onOpenChange={(open) => {
          setShowJVForm(open);
          if (!open) setEditingJV(null);
        }}
        projectId={id}
        jvAgreement={editingJV}
        onSuccess={() => {
          loadData();
          setShowJVForm(false);
          setEditingJV(null);
          toast.success(editingJV ? 'JV Agreement updated' : 'JV Agreement added');
        }}
      />

      {/* Settlement Statement (HUD) dialog — buy or sell side; replaces the
          atomic ledger entries and syncs the project's headline price field. */}
      <SettlementStatementDialog
        open={showSettlementDialog}
        onOpenChange={setShowSettlementDialog}
        project={project}
        side={settlementSide}
        onSaved={() => { loadData(); }}
      />

      <EmailWorkbookDialog
        open={showEmailWorkbook}
        onOpenChange={setShowEmailWorkbook}
        projectId={id}
      />

      <SubdivisionFormDialog
        open={showSubdivisionForm}
        onOpenChange={setShowSubdivisionForm}
        project={project}
        onSuccess={() => {
          loadData();
          toast.success('Subdivision created');
        }}
      />

      {/* QBO Import Dialog */}
      <Dialog open={showQBOImport} onOpenChange={setShowQBOImport}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-600" />
              Import from QuickBooks Online
            </DialogTitle>
            <DialogDescription>
              Upload a CSV export from QBO to import transactions to this project.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Instructions */}
            <div className="p-3 bg-muted/30 rounded-sm text-sm">
              <p className="font-medium text-blue-800 mb-2">How to export from QBO:</p>
              <ol className="list-decimal list-inside text-emerald-600 space-y-1">
                <li>Go to Reports → Transaction List by Date</li>
                <li>Filter by date range for this project</li>
                <li>Click Export → Export to Excel</li>
                <li>Save the Excel file as CSV</li>
              </ol>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={downloadQBOTemplate}
                  className="rounded-sm flex-1"
                >
                  <Download className="w-4 h-4 mr-2" /> Download Template
                </Button>
                <Button
                  onClick={handleQBOFileSelect}
                  disabled={qboImporting}
                  className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm flex-1"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {qboImporting ? 'Importing...' : 'Upload CSV'}
                </Button>
              </div>
              <input
                ref={qboFileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleQBOFileChange}
              />
            </div>

            {/* Import Results */}
            {qboImportResults && (
              <div className="border rounded-sm p-3 space-y-3">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium">{qboImportResults.success_count} Imported</span>
                  </div>
                  {qboImportResults.skipped_count > 0 && (
                    <div className="flex items-center gap-2 text-amber-600">
                      <span className="font-medium">{qboImportResults.skipped_count} Skipped (duplicates)</span>
                    </div>
                  )}
                  {qboImportResults.error_count > 0 && (
                    <div className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <span className="font-medium">{qboImportResults.error_count} Failed</span>
                    </div>
                  )}
                </div>
                
                {qboImportResults.errors?.length > 0 && (
                  <div className="bg-red-50 rounded-sm p-2 max-h-32 overflow-y-auto">
                    <p className="text-xs font-medium text-red-700 mb-1">Errors:</p>
                    <ul className="text-xs text-red-600 space-y-0.5">
                      {qboImportResults.errors.map((error, i) => (
                        <li key={`${error}-${i}`}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {qboImportResults.imported?.length > 0 && (
                  <div className="bg-green-50 rounded-sm p-2">
                    <p className="text-xs font-medium text-green-700 mb-1">Imported transactions (preview):</p>
                    <ul className="text-xs text-green-600 space-y-0.5">
                      {qboImportResults.imported.slice(0, 5).map((t, i) => (
                        <li key={`${t.date || ''}-${t.amount || 0}-${i}`}>{t.date} - {t.category} - ${t.amount}</li>
                      ))}
                      {qboImportResults.imported.length > 5 && (
                        <li className="text-green-500">...and {qboImportResults.imported.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Supported Columns */}
            <div className="text-xs text-muted-foreground">
              <p className="font-medium">Supported QBO columns:</p>
              <p>Date, Transaction Type, Num, Name, Memo/Description, Amount, Account, Category</p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowQBOImport(false);
                setQBOImportResults(null);
              }}
              className="rounded-sm"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog - prompts after recording purchase price */}
      <AlertDialog open={showStatusChangeDialog} onOpenChange={setShowStatusChangeDialog}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Purchase Price Recorded
            </AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ve recorded the purchase price for this property. Would you like to update the status to <strong>Owned</strong>?
              <br /><br />
              This indicates that the purchase has closed and you now own the property.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">
              Not Yet
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                try {
                  await projectsApi.updateStatus(id, 'Owned');
                  toast.success('Status updated to Owned');
                  loadData();
                } catch (error) {
                  console.error('Failed to update status:', error);
                  toast.error(error?.response?.data?.detail || 'Failed to update status');
                }
                setShowStatusChangeDialog(false);
                setPendingStatusChange(null);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
            >
              Yes, Mark as Owned
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Child Lot Manager Dialog */}
      <ChildLotManagerDialog
        open={showChildLotManager}
        onOpenChange={(open) => {
          setShowChildLotManager(open);
          if (!open) setEditingChildLot(null);
        }}
        childLot={editingChildLot}
        onSuccess={() => {
          loadData();
          setShowChildLotManager(false);
          setEditingChildLot(null);
          toast.success('Child lot updated');
        }}
      />
    </div>
  );
};
